/**
 * Canonical toggle switch — replaces the ~20 hand-rolled peer-checked
 * patterns scattered through the settings pages. The pattern itself is
 * unchanged (Tailwind `peer` + `peer-checked:after:translate-x-full`);
 * this primitive just wraps it in a consistent label-on-the-left
 * layout with a description option.
 *
 *   <ToggleSwitch
 *     label="Members only"
 *     description="Restrict registration to members"
 *     checked={formData.members_only}
 *     onChange={(v) => setFormData(...)}
 *   />
 */

import React, { useId } from "react";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getSecondaryText } from "../../utils/themeUtils";
import { TOKENS } from "../../utils/designTokens";

interface ToggleSwitchProps {
  /** Label rendered to the left of the switch. */
  label: React.ReactNode;
  /** Optional smaller text below the label. */
  description?: React.ReactNode;
  /** Controlled checked state. */
  checked: boolean;
  /** Fires with the new checked value. */
  onChange: (checked: boolean) => void;
  /** Disable interaction (greyed out). */
  disabled?: boolean;
  /** Visual style of the surrounding row container. */
  variant?: "row" | "inline";
  /** Optional className override on the outer wrapper. */
  className?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  variant = "row",
  className = "",
}) => {
  const { isDarkMode } = useTheme();
  const id = useId();

  // The visible switch — platform action color when on, neutral when off.
  // The knob is --cc-action-text (white in light mode, near-black in dark)
  // so it stays visible on the near-white dark-mode on-state.
  const switchClasses = `${TOKENS.toggle.switch} ${
    checked
      ? "bg-[var(--cc-action)]"
      : isDarkMode
      ? "bg-gray-600"
      : "bg-gray-300"
  }`;

  if (variant === "inline") {
    return (
      <label
        htmlFor={id}
        className={`${TOKENS.toggle.wrapper} ${disabled ? "cursor-not-allowed opacity-60" : ""} ${className}`}
      >
        <input
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className={TOKENS.toggle.inputClass}
        />
        <div className={switchClasses} />
        {label && (
          <span className={`ml-3 text-sm ${getPrimaryText(isDarkMode)}`}>{label}</span>
        )}
      </label>
    );
  }

  // row variant — label + description left, switch right. The text
  // column is itself a <label htmlFor> (multiple labels per control are
  // valid HTML) so screen readers announce the toggle by its visible
  // text AND clicking the text flips the switch (audit H10).
  return (
    <div
      className={`flex items-start justify-between p-4 rounded-lg ${
        isDarkMode ? "bg-gray-700/50" : "bg-gray-50"
      } ${className}`}
    >
      <label
        htmlFor={id}
        className={`flex-1 pr-4 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div id={`${id}-label`} className={`font-medium ${getPrimaryText(isDarkMode)}`}>{label}</div>
        {description && (
          <p id={`${id}-desc`} className={`text-sm mt-1 ${getSecondaryText(isDarkMode)}`}>
            {description}
          </p>
        )}
      </label>
      <label
        htmlFor={id}
        className={`${TOKENS.toggle.wrapper} ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      >
        <input
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className={TOKENS.toggle.inputClass}
          aria-labelledby={`${id}-label`}
          aria-describedby={description ? `${id}-desc` : undefined}
        />
        <div className={switchClasses} />
      </label>
    </div>
  );
};

export default ToggleSwitch;
